import React from 'react';
import {useSelector} from 'react-redux';
import store, {AppState} from './stores';
import ModalReceiveTalkingRequest from './presentations/ModalReceiveTalkingRequest';
import {TalkingRequestAcceptedInterface} from './pages/Home';
import {
  clearData,
  close as closeModalReceiveTalkingRequest,
  open as openModalReceiveTalkingRequest,
  ReceiveTalkingRequestInterface,
  setData
} from './stores/modal-receive-talking-request';
import {
  close as closeSendingTalkingRequestModal,
  open as openSendingTalkingRequestModal,
  SendingTalkingRequestInterface
} from './stores/sending-talking-request-modal';
import {
  open as openSendingInvitationRequestModal,
  close as closeSendingInvitationRequestModal,
  SendingInvitationRequestInterface,
} from './stores/sending-invitation-request-modal';
import {
  open as openReceiveInvitationRequestModal, ReceiveInvitationRequestInterface,
  close as closeReceiveInvitationRequestModal
} from './stores/receive-invitation-request-modal';
import {
  open as openMissedCallModal
} from './stores/missed-call-modal';
import {
  open as openRejectMessageModal
} from './stores/reject-message-modal';
import {TalkingType} from './stores/talkings';
import SendingTalkingRequestModal from './containers/SendingTalkingRequestModal';
import SendingInvitationRequestModal from './components/SendingInvitationRequest/SendingInvitationRequestModal';
import ReceiveInvitationRequestModal from './components/ReceiveInvitationRequest/ReceiveInvitationRequestModal';
import MissedCallModal from './components/MissedCall/MissedCallModal';
import RejectMessageModal from './components/RejectMessage/RejectMessageModal';
import {useSocketEvent} from './hooks/useSocketEvent';
import { appEnv } from './config/env';

interface ReceiverInterface {
  children?: React.ReactElement | null
}

const Receiver: React.FC<ReceiverInterface> = (props) => {
  const userId    = useSelector((state: AppState) => state.authenticated.userId as number);
  const connected = useSelector((state: AppState) => state.receiver.connected);

  useSocketEvent<SendingTalkingRequestInterface>('sendingTalkingRequest', (payload) => {
    console.log(`sendingTalkingRequest`, payload);
    store.dispatch(openSendingTalkingRequestModal(payload));
    localStorage.setItem('sendingTalkingRequest', JSON.stringify(payload));
    window.open(`/room-connecting?sendTalkingRequest=1`, '_room');
  }, [connected]);

  useSocketEvent<any>('talkingRequestCancelled', (payload) => {
    console.log(`talkingRequestCancelled`, payload);
    store.dispatch(clearData());
    store.dispatch(closeModalReceiveTalkingRequest());
    store.dispatch(closeSendingTalkingRequestModal());

    if (payload.callHistory.guestUser.id === userId) {
      store.dispatch(openMissedCallModal());
    }
  }, [connected, userId], 'none');

  useSocketEvent<ReceiveTalkingRequestInterface>('receiveTalkingRequest', (payload) => {
    console.log(`receiveTalkingRequest`, payload);
    store.dispatch(setData(payload));
    store.dispatch(openModalReceiveTalkingRequest());

    const body = `${payload.hostUser.firstName} ${payload.hostUser.lastName}さんから着信`;
    const data = {
      url: `${window.location.protocol}//${window.location.host}/`
    };

    if ('Notification' in window) {
      Notification.requestPermission()?.then(() => {
        navigator.serviceWorker.ready.then((registration) => {
          registration.showNotification('Roomi Meet', {body, data});
        });
      });
    }
  }, [connected]);

  useSocketEvent<TalkingRequestAcceptedInterface>('talkingRequestAccepted', ({room}) => {
    console.log('talkingRequestAccepted', room);
    store.dispatch(clearData());
    store.dispatch(closeModalReceiveTalkingRequest());
    store.dispatch(closeSendingTalkingRequestModal());
    window.open(`/room?title=${room.title}&roomId=${room.id}`, '_room');
  }, [connected]);

  useSocketEvent<TalkingRequestAcceptedInterface>('talkingRequestRejected', (payload) => {
    console.log('talkingRequestRejected', payload);
    store.dispatch(clearData());
    store.dispatch(closeModalReceiveTalkingRequest());
    store.dispatch(closeSendingTalkingRequestModal());

    if (payload.callHistory.hostUser.id === userId) {
      store.dispatch(openRejectMessageModal(payload));
    }
  }, [connected, userId], 'after');

  useSocketEvent<SendingInvitationRequestInterface>('sendingInvitationRequest', (payload) => {
    console.log(`sendingInvitationRequest`, payload);
    store.dispatch(openSendingInvitationRequestModal(payload));
  }, [connected]);

  useSocketEvent<any>('invitationRequestCancelled', (payload) => {
    console.log(`invitationRequestCancelled`, payload);
    store.dispatch(closeSendingInvitationRequestModal());
    store.dispatch(closeReceiveInvitationRequestModal());
  }, [connected], 'after');

  useSocketEvent<ReceiveInvitationRequestInterface>('receiveInvitationRequest', (payload) => {
    console.log(`receiveInvitationRequest`, payload);
    store.dispatch(openReceiveInvitationRequestModal(payload));

    const body = `${payload.hostUser.firstName} ${payload.hostUser.lastName}さんから招待`;
    const data = {
      url: `${window.location.protocol}//${window.location.host}/`
    };

    if ('Notification' in window) {
      Notification.requestPermission()?.then(() => {
        navigator.serviceWorker.ready.then((registration) => {
          registration.showNotification('Roomi Meet', {body, data});
        });
      });
    }
  }, [connected]);

  useSocketEvent<TalkingRequestAcceptedInterface>('invitationRequestAccepted', ({room}) => {
    console.log('invitationRequestAccepted', room);
    store.dispatch(closeSendingInvitationRequestModal());
    store.dispatch(closeReceiveInvitationRequestModal());

    if (room.hostUserId !== userId) {
      window.open(`/room?title=${room.title}&roomId=${room.id}`, '_room');
    }
  }, [connected, userId]);

  useSocketEvent<TalkingRequestAcceptedInterface>('invitationRequestRejected', (payload) => {
    console.log('invitationRequestRejected', payload);
    store.dispatch(closeSendingInvitationRequestModal());
    store.dispatch(closeReceiveInvitationRequestModal());

    if (payload.callHistory.hostUser.id === userId) {
      store.dispatch(openRejectMessageModal(payload));
    }
  }, [connected, userId], 'none');

  useSocketEvent<{ room: TalkingType }>('roomCreated', ({room}) => {
    console.log('roomCreated', room);
    window.open(`${appEnv.dir}room?title=${room.title}&roomId=${room.id}&hostUserId=${userId}`, '_room');
  }, [connected, userId]);

  return (
          <>
            {props.children}
            {connected && <>
              <SendingTalkingRequestModal/>
              <ModalReceiveTalkingRequest/>
              <SendingInvitationRequestModal/>
              <ReceiveInvitationRequestModal/>
              <MissedCallModal/>
              <RejectMessageModal/>
            </>}
          </>
  );
}

export default Receiver;
